import { useEffect, useState } from 'react'
import { MessageFragment, useDeleteInternalThreadMessageMutation, useNewThreadMessageEventSubscription, useSendThreadMessageMutation, useThreadMessagesQuery } from 'src/api/api.generated'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { compareDates } from 'src/utils/date'

export default function ChatCRUD({ propertyId, tenantId, threadId }: { propertyId: string; tenantId: string; threadId: string | null }) {
  const showError = useErrorSnackBar()
  const [sendMessageLoading, setSendMessageLoading] = useState(false)
  const [messages, setMessages] = useState<MessageFragment[]>([])
  const { loading: threadMessagesLoading, refetch } = useThreadMessagesQuery({
    variables: {
      filter: {
        propertyId: propertyId,
        tenantId: tenantId,
        threadId: threadId ?? '',
      },
    },
    skip: !threadId,
    onError: showError,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setMessages([...(data.threadMessages ?? [])])
    },
  })

  const { data: threadSubscriptionData } = useNewThreadMessageEventSubscription({
    variables: {
      filter: {
        propertyId,
        tenantId,
        threadId: threadId,
      },
    },
    skip: !threadId,
  })
  const threadIdSubscription = threadSubscriptionData?.newThreadMessageEvent?.threadId

  useEffect(() => {
    if (threadIdSubscription) {
      refetch()
    }
  }, [threadSubscriptionData])

  const [deleteNoteMutation, { loading: deleteNoteLoading }] = useDeleteInternalThreadMessageMutation({
    onError: showError,
  })

  const [sendMessage] = useSendThreadMessageMutation({
    onError: showError,
  })

  async function deleteNote(message: MessageFragment) {
    const result = await deleteNoteMutation({
      variables: {
        threadMessageId: message.id,
        tenantId,
      },
    })

    if (!result.errors) {
      setMessages((prev) => prev.filter((m) => m.id !== message.id))
    }
  }

  const threadMessages = messages.sort((a, b) => {
    if (a.auditable.createdOn && b.auditable.createdOn) {
      // sorting newest first because we are using column-reverse in the chat body, so the messages will be displayed in the correct order
      return compareDates(b.auditable.createdOn, a.auditable.createdOn, true)
    }

    return 0
  })

  async function addMessageThread(message: MessageFragment) {
    setSendMessageLoading(true)
    const result = await sendMessage({
      variables: {
        input: {
          internalNote: message.internalNote,
          message: message.message,
          tenantId: tenantId ?? '',
          threadId: threadId ?? '',
        },
      },
    })

    setSendMessageLoading(false)

    return result
  }

  return {
    messages: threadMessages,
    deleteNote,
    addMessageThread,
    loading: threadMessagesLoading || deleteNoteLoading,
    sendMessageLoading,
  }
}
